
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { CheckCircle, ClipboardCheck, Zap, RefreshCw, Shield, Users } from 'lucide-react';

const SOPAutomation = () => {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <section className="py-20 bg-gradient-to-br from-white via-[#F0F9FF] to-[#E0F2FE]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1 className="text-4xl font-bold mb-6 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">
              Standard Operating Procedure Automation
            </h1>
            <p className="text-lg text-gray-700">
              Transform your standard operating procedures from static documents into intelligent, 
              interactive workflows that guide your team to consistent success.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <h2 className="text-2xl font-bold mb-4 text-[#0EA5E9]">The Problem with Traditional SOPs</h2>
              <p className="text-gray-700 mb-6">
                Standard operating procedures are essential for consistency and quality, but traditional 
                SOP documentation faces significant challenges:
              </p>
              <ul className="space-y-4">
                {[
                  "Static documents become outdated quickly",
                  "Difficult to ensure compliance and track adherence",
                  "Limited visibility into bottlenecks and improvement opportunities",
                  "One-size-fits-all approach lacks adaptability to specific situations"
                ].map((item, index) => (
                  <li key={index} className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-[#0EA5E9] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-xl">
              <img
                src="/placeholder.svg" 
                alt="SOP Automation"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#0EA5E9] to-[#6366F1]">
              Our SOP Automation Solution
            </h2>
            <p className="text-gray-700">
              We transform your static procedures into dynamic, intelligent workflows powered by AI agents.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {[
              {
                title: "Interactive Guidance",
                description: "Step-by-step intelligent workflows that adapt to user inputs and context.",
                icon: <ClipboardCheck className="h-10 w-10 text-[#0EA5E9]" />
              },
              {
                title: "Real-time Updates",
                description: "SOPs that stay current with automatic updates when processes change.",
                icon: <RefreshCw className="h-10 w-10 text-[#0EA5E9]" />
              },
              {
                title: "Compliance Tracking",
                description: "Built-in monitoring to ensure procedures are followed correctly.",
                icon: <Shield className="h-10 w-10 text-[#0EA5E9]" />
              },
              {
                title: "Process Acceleration",
                description: "Automation of routine decisions and actions within your procedures.",
                icon: <Zap className="h-10 w-10 text-[#0EA5E9]" />
              },
              {
                title: "Knowledge Integration",
                description: "Incorporate company knowledge bases and resources directly into workflows.",
                icon: <ClipboardCheck className="h-10 w-10 text-[#0EA5E9]" />
              },
              {
                title: "Continuous Improvement",
                description: "Analytics that identify bottlenecks and suggest process optimizations.",
                icon: <RefreshCw className="h-10 w-10 text-[#0EA5E9]" />
              }
            ].map((item, index) => (
              <Card key={index} className="border border-[#0EA5E9]/20 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="bg-[#F0F9FF] p-8 rounded-xl border border-[#0EA5E9]/20 mb-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-4 text-[#0EA5E9]">Implementation Process</h3>
                <p className="text-gray-700 mb-4">
                  Our streamlined approach ensures your SOP automation project delivers value quickly:
                </p>
                <ul className="space-y-3">
                  {[
                    "Assessment of existing procedures and documentation",
                    "Design of intelligent workflows with stakeholder input",
                    "Development and integration with your existing systems",
                    "Testing and optimization before full deployment",
                    "Training and change management support"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-[#0EA5E9] mr-3 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <img
                src="/placeholder.svg" 
                alt="Implementation Process"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="border border-[#0EA5E9]/20">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-3">For Operations Teams</h3>
                <p className="text-gray-700 mb-4">
                  Ensure consistent execution and easily identify opportunities for process improvement.
                </p>
                <Users className="h-8 w-8 text-[#0EA5E9]" />
              </CardContent>
            </Card>
            <Card className="border border-[#0EA5E9]/20">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-3">For Compliance Teams</h3>
                <p className="text-gray-700 mb-4">
                  Gain visibility into procedure adherence and automate compliance documentation.
                </p>
                <Shield className="h-8 w-8 text-[#0EA5E9]" />
              </CardContent>
            </Card>
            <Card className="border border-[#0EA5E9]/20">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-3">For Training Teams</h3>
                <p className="text-gray-700 mb-4">
                  Reduce onboarding time with interactive guidance that helps new employees learn while doing.
                </p>
                <Users className="h-8 w-8 text-[#0EA5E9]" />
              </CardContent>
            </Card>
          </div>

          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-6 text-[#0EA5E9]">Ready to transform your SOPs?</h2>
            <p className="text-gray-700 mb-8">
              Contact us to discuss how we can help automate your standard operating procedures and 
              unlock new levels of efficiency and consistency.
            </p>
            <div className="flex justify-center">
              <a 
                href="/contact" 
                className="bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white px-8 py-3 rounded-full font-medium hover:opacity-90 transition-opacity"
              >
                Schedule a Consultation
              </a>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default SOPAutomation;
